import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  X,
  Save,
  RefreshCw,
  Plus,
  Copy,
  Check,
  AlertCircle,
  MapPin,
  AlertTriangle,
  DollarSign,
  Truck,
  Clock,
  Package,
  Shield,
  Calendar,
  Layers
} from 'lucide-react';
import axios from 'axios';
import { fetchErpagWarehouses } from '../../../api/erpag';

// ============================================================
// API BASE URL & AUTH HELPER
// ============================================================
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('accessToken') ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================================
// 🆕 CONFIG CONSTANTS (match backend env defaults)
// ============================================================
const HIGH_VALUE_THRESHOLD =
  Number(process.env.REACT_APP_HIGH_VALUE_ORDER_THRESHOLD) || 5000;
const DEFAULT_FALLBACK_BUSINESS_DAYS =
  Number(process.env.REACT_APP_DEFAULT_FALLBACK_BUSINESS_DAYS) || 5;

// ============================================================
// STATUS BADGE
// ============================================================
const getStatusLabel = (status, priceSummary) => {
  if (priceSummary?.hasMismatch && !priceSummary?.overrideAuthorized) {
    return 'Price Mismatch';
  }
  const labels = {
    NEW: 'Queue',
    PROCESSING: 'Queue',
    PENDING: 'Queue',
    APPROVED: 'Queue',
    QUEUED: 'Queue',
    SYNCED: 'Success',
    COMPLETED: 'Success',
    FAILED: 'Fail',
    ERROR: 'Error',
    NEEDS_REVIEW: 'Needs Review'
  };
  return labels[status] || 'Queue';
};

// ============================================================
// SOURCE BADGE
// ============================================================
const getSourceMeta = (source) => {
  const value = String(source || '').toUpperCase();

  if (value === 'FAXINATING') {
    return {
      label: 'EDI',
      className: 'bg-purple-50 text-purple-700 border-purple-200'
    };
  }

  if (value === 'EMAIL_IMPORT') {
    return {
      label: 'Email',
      className: 'bg-green-50 text-green-700 border-green-200'
    };
  }

  return {
    label: 'Upload',
    className: 'bg-gray-100 text-gray-700 border-gray-200'
  };
};

// ============================================================
// HELPER: GET SHIP TO ADDRESS
// ============================================================
const getShipToAddress = (sd = {}) => {
  const shipTo = sd.shipTo || {};
  const streetLines = Array.isArray(shipTo.streetLines) ? shipTo.streetLines : [];
  const addressLines = Array.isArray(shipTo.addressLines) ? shipTo.addressLines : [];

  const address1 = shipTo.address1 || shipTo.address || streetLines[0] || addressLines[0] || '';
  const address2 = shipTo.address2 || streetLines[1] || addressLines[1] || '';

  return {
    name: shipTo.name || '',
    address1,
    address2,
    city: shipTo.city || '',
    stateProvince: shipTo.stateProvince || shipTo.region || '',
    zipCode: shipTo.zipCode || shipTo.postalCode || '',
    country:
      shipTo.country ||
      (shipTo.countryCode === 'CA'
        ? 'Canada'
        : shipTo.countryCode === 'US'
          ? 'United States'
          : shipTo.countryCode || '')
  };
};

// ============================================================
// HELPER: FORMAT CURRENCY
// ============================================================
const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const num = Number(String(val).replace(/,/g, ''));
  return isNaN(num)
    ? ''
    : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ============================================================
// HELPER: NORMALIZE UOM
// ============================================================
const normalizeUomForUi = (unitStr) => {
  const u = String(unitStr || '').trim().toUpperCase();
  if (['CS', 'CASE', 'CASES', 'CT', 'BX', 'BOX'].includes(u)) return 'CS';
  if (['MASTER', 'MC'].includes(u)) return 'MASTER';
  if (['KG', 'KGS'].includes(u)) return 'KG';
  if (['EA', 'EACH', 'PC', 'PCS'].includes(u)) return 'EA';
  if (['UN', 'UNIT', 'UNITS'].includes(u)) return 'UN';
  return 'CS';
};

// ============================================================
// 🆕 PRIORITY BADGE CONFIG — handles string ("P1") OR number (1)
// ============================================================
const PRIORITY_CONFIG = {
  P1: { label: 'P1 – Cancel Date', color: 'bg-red-100 text-red-800 border-red-300', icon: '🔴' },
  P2: { label: 'P2 – Requested Delivery', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: '🟠' },
  P3: { label: 'P3 – Lead Time', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: '🔵' },
  P4: { label: 'P4 – Fallback', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: '⚪' }
};

const getPriorityConfig = (priorityUsed) => {
  if (!priorityUsed) return PRIORITY_CONFIG.P4;
  const key =
    typeof priorityUsed === 'number'
      ? `P${priorityUsed}`
      : String(priorityUsed).toUpperCase();
  return PRIORITY_CONFIG[key] || PRIORITY_CONFIG.P4;
};

// ============================================================
// 🆕 DATE HELPERS (match backend logic for live preview)
// ============================================================
const parseDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

const addBusinessDaysLocal = (date, businessDays) => {
  const result = new Date(date.getTime());
  let added = 0;
  const n = Number(businessDays);
  while (added < n) {
    result.setUTCDate(result.getUTCDate() + 1);
    const day = result.getUTCDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return result;
};

const rollBackToFridayLocal = (date) => {
  if (!date) return null;
  const d = new Date(date.getTime());
  const day = d.getUTCDay();
  if (day === 6) d.setUTCDate(d.getUTCDate() - 1);
  else if (day === 0) d.setUTCDate(d.getUTCDate() - 2);
  return d;
};

const toIsoDateLocal = (date) => (date ? date.toISOString().split('T')[0] : null);

const recalcP4Preview = (soDate, businessDays) => {
  const d = parseDateSafe(soDate);
  if (!d) return null;
  const n = Number(businessDays);
  if (!Number.isFinite(n) || n < 0) return null;
  const raw = addBusinessDaysLocal(d, n);
  const adjusted = rollBackToFridayLocal(raw);
  return toIsoDateLocal(adjusted);
};

const isDeadlineInPast = (deadlineStr) => {
  if (!deadlineStr) return false;
  const d = parseDateSafe(deadlineStr);
  const today = parseDateSafe(new Date());
  if (!d || !today) return false;
  return d.getTime() < today.getTime();
};

const formatDeadlineLabel = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : '—';

// ============================================================
// 🆕 HIGH-VALUE CONFIRMATION MODAL
// ============================================================
const HighValueConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  orderTotal,
  soDate,
  currentDeadline,
  initialCustomDays
}) => {
  const [customDays, setCustomDays] = useState(
    initialCustomDays != null && initialCustomDays !== ''
      ? String(initialCustomDays)
      : String(DEFAULT_FALLBACK_BUSINESS_DAYS)
  );
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCustomDays(
        initialCustomDays != null && initialCustomDays !== ''
          ? String(initialCustomDays)
          : String(DEFAULT_FALLBACK_BUSINESS_DAYS)
      );
      setConfirmed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const previewDeadline = useMemo(() => {
    const n = Number(customDays);
    if (!Number.isFinite(n) || n < 0) return null;
    return recalcP4Preview(soDate, n);
  }, [customDays, soDate]);

  if (!isOpen) return null;

  const invalidDays = !Number.isFinite(Number(customDays)) || Number(customDays) < 0;

  return (
    <div
      className="fixed inset-0 z-[10050] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-bold text-red-900">High-Value Order Confirmation</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-red-400 hover:text-red-700 hover:bg-red-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            <p className="font-semibold">
              This order is <strong>${formatCurrency(orderTotal)}</strong> and has no Cancel Date,
              no Requested Delivery Date, and no customer lead time.
            </p>
            <p className="mt-2">
              The system fell back to <strong>Priority 4</strong> (SO Date + N business days).
              Please confirm the business days to use before saving.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
                SO Date
              </label>
              <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm font-mono">
                {soDate ? new Date(soDate).toLocaleDateString('en-CA') : '—'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
                Current Calculated Deadline
              </label>
              <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm font-mono">
                {formatDeadlineLabel(currentDeadline)}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
              Business Days from SO Date
            </label>
            <input
              type="number"
              min="0"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Default is {DEFAULT_FALLBACK_BUSINESS_DAYS} business days. Weekends excluded. If result
              lands on Sat/Sun it rolls back to Friday.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-blue-700" />
              <span className="text-xs font-bold text-blue-900 uppercase">Preview Deadline</span>
            </div>
            <div className="text-xl font-bold text-blue-900">
              {invalidDays ? '—' : formatDeadlineLabel(previewDeadline)}
            </div>
          </div>

          <div className="flex items-start gap-2 pt-2 border-t border-gray-200">
            <input
              type="checkbox"
              id="hvConfirmInner"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="w-4 h-4 mt-0.5 text-red-600 rounded border-red-300 focus:ring-red-500"
            />
            <label htmlFor="hvConfirmInner" className="text-xs text-gray-700 cursor-pointer">
              I confirm this shipping deadline for the high-value order and authorize approval/sync.
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onConfirm({
                businessDays: Number(customDays),
                previewDeadline
              })
            }
            disabled={!confirmed || invalidDays}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Confirm Deadline
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// 🆕 CONSOLIDATION QUICK-VIEW DRAWER (Task C)
// ============================================================
const ConsolidationDrawer = ({ isOpen, onClose, uploads, isFiltered }) => {
  const data = useMemo(() => {
    const groups = {};
    uploads.forEach((item) => {
      const routing = item.routingInfo || {};
      const sd = item.structuredData || {};
      
      const familyName =
        routing.matchedFamily ||
        sd.billTo?.name ||
        item.customer ||
        'Unknown Customer';

      const priority = routing.priorityUsed
        ? String(routing.priorityUsed).toUpperCase()
        : 'P4';
      const priorityKey = priority.startsWith('P') ? priority : `P${priority}`;

      if (!groups[familyName]) {
        groups[familyName] = {
          name: familyName,
          orderCount: 0,
          totalAmount: 0,
          totalUN: 0,
          totalCS: 0,
          priorities: { P1: 0, P2: 0, P3: 0, P4: 0 }
        };
      }

      groups[familyName].orderCount++;

      const amount =
        item.orderAmount ??
        (sd.items || []).reduce(
          (sum, li) =>
            sum + (Number(li.unitCost) || 0) * (Number(li.qty ?? li.quantity) || 0),
          0
        );
      groups[familyName].totalAmount += amount;

      (sd.items || []).forEach((li) => {
        const uom = normalizeUomForUi(li.unit || li.uom);
        const qty = Number(li.qty ?? li.quantity) || 0;
        if (uom === 'UN') groups[familyName].totalUN += qty;
        if (uom === 'CS') groups[familyName].totalCS += qty;
      });

      if (groups[familyName].priorities[priorityKey] !== undefined) {
        groups[familyName].priorities[priorityKey]++;
      } else {
        groups[familyName].priorities.P4++;
      }
    });

    return Object.values(groups).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [uploads]);

  const globalTotals = data.reduce(
    (acc, curr) => {
      acc.orders += curr.orderCount;
      acc.amount += curr.totalAmount;
      acc.un += curr.totalUN;
      acc.cs += curr.totalCS;
      return acc;
    },
    { orders: 0, amount: 0, un: 0, cs: 0 }
  );

  return (
    <div
      className={`fixed inset-0 z-[10050] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div
        className={`absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 shrink-0">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-bold text-gray-800">Consolidation View</h2>
            {isFiltered && (
              <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                Filtered
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 bg-indigo-50 border-b border-indigo-100 shrink-0">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-bold text-indigo-800 uppercase tracking-wider">
              Total Consolidated
            </span>
            <span className="text-lg font-bold text-indigo-900">
              ${formatCurrency(globalTotals.amount)}
            </span>
          </div>
          <div className="flex gap-4 text-sm text-indigo-800">
            <div>
              <span className="font-bold">{globalTotals.orders}</span> Orders
            </div>
            <div>
              <span className="font-bold">{globalTotals.un}</span> UN
            </div>
            <div>
              <span className="font-bold">{globalTotals.cs}</span> CS
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
          {data.map((group) => (
            <div
              key={group.name}
              className="border border-gray-200 rounded-xl p-4 shadow-sm bg-white"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-gray-900 leading-tight">{group.name}</h3>
                <span className="text-sm font-bold text-green-700 bg-green-50 px-2 py-1 rounded border border-green-200 shrink-0 ml-3">
                  ${formatCurrency(group.totalAmount)}
                </span>
              </div>
              <div className="text-xs font-semibold text-gray-500 mb-3">
                {group.orderCount} Order{group.orderCount > 1 ? 's' : ''}
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {group.priorities.P1 > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-300">
                    P1: {group.priorities.P1}
                  </span>
                )}
                {group.priorities.P2 > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300">
                    P2: {group.priorities.P2}
                  </span>
                )}
                {group.priorities.P3 > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300">
                    P3: {group.priorities.P3}
                  </span>
                )}
                {group.priorities.P4 > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-300">
                    P4: {group.priorities.P4}
                  </span>
                )}
              </div>
              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <div className="flex-1 text-center bg-gray-50 rounded py-1.5 border border-gray-100">
                  <div className="text-[10px] uppercase text-gray-500 font-bold">Total UN</div>
                  <div className="text-sm font-bold text-blue-700">{group.totalUN}</div>
                </div>
                <div className="flex-1 text-center bg-gray-50 rounded py-1.5 border border-gray-100">
                  <div className="text-[10px] uppercase text-gray-500 font-bold">Total CS</div>
                  <div className="text-sm font-bold text-purple-700">{group.totalCS}</div>
                </div>
              </div>
            </div>
          ))}
          {data.length === 0 && (
            <div className="text-center text-gray-500 py-8 flex flex-col items-center">
              <Layers className="w-10 h-10 text-gray-300 mb-2" />
              <p className="text-sm font-medium">No data to consolidate.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// EDIT ORDER MODAL
// ============================================================
const EditOrderModal = ({ item, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const sd = item.structuredData || {};
  const priceSummary = sd.priceValidationSummary || {};

  const [header, setHeader] = useState({
    poNumber: sd.purchaseOrder?.poNumber || '',
    customer: sd.billTo?.name || item.customer || '',
    currency: sd.totals?.currency || item.currency || 'CAD',
    requestedDeliveryDate:
      sd.purchaseOrder?.deliveryDate ||
      sd.purchaseOrder?.requestedDeliveryDate ||
      item.order_header?.delivery_date ||
      '',
    cancelDate: sd.purchaseOrder?.cancelDate || sd.purchaseOrder?.cancelByDate || ''
  });

  const [overrideAuthorized, setOverrideAuthorized] = useState(
    Boolean(priceSummary.overrideAuthorized)
  );

  const existingShipTo = getShipToAddress(sd);
  const [shipTo, setShipTo] = useState(existingShipTo);

  const [lineItems, setLineItems] = useState(
    (sd.items || []).map((li, idx) => ({
      lineNumber: li.lineNumber || idx + 1,
      sku: li.vin || li.sku || '',
      gfsItemNumber: li.itemNumber || '',
      qty: li.qty ?? li.quantity ?? 1,
      description: li.description || '',
      unitCost: formatCurrency(li.unitCost),
      unit: normalizeUomForUi(li.unit || li.uom),
      originalQty: li.originalQty ?? li.excelUnits ?? null,
      originalUnitCost: li.originalUnitCost ?? li.excelFirstCost ?? null,
      appliedUomRule: li.appliedUomRule || li.uomRule || null,
      priceValidation: li.priceValidation || null,
      expectedPrice: li.expectedPrice ?? li.priceValidation?.expectedPrice ?? null,
      isMismatch: Boolean(li.priceMismatch || li.priceValidation?.isMismatch)
    }))
  );

  // --- Shipping Deadline & Warehouse State ---
  const routingInfo = item.routingInfo || null;

  // 🆕 Manual override + high-value state (seeded from persisted structuredData)
  const [warehouseId, setWarehouseId] = useState(
    sd.manualWarehouse ||
      routingInfo?.assignedWarehouse ||
      sd.warehouse ||
      ''
  );
  const [warehouseLabel, setWarehouseLabel] = useState(
    sd.warehouseLabel || routingInfo?.assignedWarehouseLabel || ''
  );
  const [warehouses, setWarehouses] = useState([]);

  const [manualShippingDeadline, setManualShippingDeadline] = useState(
    sd.manualShippingDeadline || ''
  );

  const [highValueConfirmed, setHighValueConfirmed] = useState(
    Boolean(sd.highValueConfirmed)
  );
  const [highValueCustomDays, setHighValueCustomDays] = useState(
    sd.highValueCustomDays != null ? String(sd.highValueCustomDays) : ''
  );
  const [showHighValueModal, setShowHighValueModal] = useState(false);

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const result = await fetchErpagWarehouses();
        if (result.success && Array.isArray(result.data)) {
          setWarehouses(result.data);
        }
      } catch (err) {
        console.error('Failed to load warehouses:', err);
      }
    };
    loadWarehouses();
  }, []);

  const handleWarehouseChange = (e) => {
    const selectedId = e.target.value;
    setWarehouseId(selectedId);
    const wh = warehouses.find((w) => w.id === selectedId);
    setWarehouseLabel(wh ? wh.description || wh.key : '');
  };

  const updateShipTo = (field, value) => {
    setShipTo((prev) => ({ ...prev, [field]: value }));
  };

  const updateLine = (idx, field, value) => {
    setLineItems((prev) =>
      prev.map((line, i) => (i === idx ? { ...line, [field]: value } : line))
    );
  };

  const addLine = () => {
    setLineItems((prev) => [
      ...prev,
      {
        lineNumber: prev.length + 1,
        sku: '',
        gfsItemNumber: '',
        qty: 1,
        description: '',
        unitCost: '',
        unit: 'UN',
        originalQty: null,
        originalUnitCost: null,
        appliedUomRule: null,
        priceValidation: null,
        expectedPrice: null,
        isMismatch: false
      }
    ]);
  };

  const removeLine = (idx) => {
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleApplyErpagPrices = () => {
    setLineItems((prev) =>
      prev.map((line) => {
        if (line.expectedPrice && line.expectedPrice > 0) {
          return {
            ...line,
            unitCost: formatCurrency(line.expectedPrice),
            isMismatch: false
          };
        }
        return line;
      })
    );
    setOverrideAuthorized(true);
  };

  const totalUN = lineItems
    .filter((i) => normalizeUomForUi(i.unit) === 'UN')
    .reduce((sum, i) => sum + (Number(i.qty) || 0), 0);
  const totalCS = lineItems
    .filter((i) => normalizeUomForUi(i.unit) === 'CS')
    .reduce((sum, i) => sum + (Number(i.qty) || 0), 0);
  const totalAmount = lineItems.reduce(
    (sum, i) =>
      sum +
      (Number(i.qty) || 0) * (Number(String(i.unitCost).replace(/,/g, '')) || 0),
    0
  );

  // --- Effective deadline shown in modal (manual > confirmed custom > routing) ---
  const effectiveDeadline =
    manualShippingDeadline ||
    (highValueConfirmed && highValueCustomDays
      ? recalcP4Preview(item.uploadedAt, Number(highValueCustomDays))
      : routingInfo?.shippingDeadline) ||
    '';

  const needsHighValueGate =
    routingInfo?.requiresHighValueConfirmation ||
    (routingInfo?.priorityUsed &&
      String(routingInfo.priorityUsed).toUpperCase() === 'P4' &&
      totalAmount > HIGH_VALUE_THRESHOLD) ||
    (!routingInfo && totalAmount > HIGH_VALUE_THRESHOLD);

  const highValueSatisfied = !needsHighValueGate || highValueConfirmed;

  const handleHighValueConfirm = ({ businessDays, previewDeadline }) => {
    setHighValueCustomDays(String(businessDays));
    setHighValueConfirmed(true);
    // We intentionally do NOT set manualShippingDeadline here —
    // backend will recompute consistently from highValueCustomDays.
    setShowHighValueModal(false);
  };

  const handleSave = async () => {
    setError('');

    if (!shipTo.name.trim()) {
      setError('Ship To name is mandatory.');
      return;
    }
    if (!shipTo.address1.trim()) {
      setError('Ship To address is mandatory.');
      return;
    }
    if (!shipTo.city.trim()) {
      setError('Ship To city is mandatory.');
      return;
    }
    if (!shipTo.zipCode.trim()) {
      setError('Ship To postal / ZIP code is mandatory.');
      return;
    }
    if (!shipTo.country.trim()) {
      setError('Ship To country is mandatory.');
      return;
    }
    if (!lineItems.length) {
      setError('At least one order item is required.');
      return;
    }

    if (needsHighValueGate && !highValueConfirmed) {
      setError(
        'This is a high-value order with a fallback (P4) shipping deadline. Please confirm the deadline before saving.'
      );
      return;
    }

    setSaving(true);

    const structuredData = {
      ...sd,
      // 🆕 Mirror operator overrides onto structuredData (backend also persists at root)
      manualWarehouse: warehouseId || null,
      manualShippingDeadline: manualShippingDeadline || null,
      highValueConfirmed: Boolean(highValueConfirmed),
      highValueCustomDays:
        highValueCustomDays === '' || highValueCustomDays == null
          ? null
          : Number(highValueCustomDays),

      warehouse: warehouseId || undefined,
      warehouseLabel: warehouseLabel || undefined,
      purchaseOrder: {
        ...(sd.purchaseOrder || {}),
        poNumber: header.poNumber,
        deliveryDate: header.requestedDeliveryDate,
        cancelDate: header.cancelDate
      },
      deliveryRequested: header.requestedDeliveryDate,
      billTo: { ...(sd.billTo || {}), name: header.customer },
      totals: { ...(sd.totals || {}), currency: header.currency },
      shipTo: {
        ...(sd.shipTo || {}),
        name: shipTo.name,
        streetLines: [shipTo.address1, ...(shipTo.address2 ? [shipTo.address2] : [])],
        city: shipTo.city,
        region: shipTo.stateProvince,
        postalCode: shipTo.zipCode,
        countryCode:
          shipTo.country.toLowerCase() === 'canada'
            ? 'CA'
            : shipTo.country.toLowerCase() === 'united states'
              ? 'US'
              : shipTo.country,
        address1: shipTo.address1,
        address2: shipTo.address2,
        stateProvince: shipTo.stateProvince,
        zipCode: shipTo.zipCode,
        country: shipTo.country
      },
      priceValidationSummary: {
        ...(sd.priceValidationSummary || {}),
        overrideAuthorized: overrideAuthorized
      },
      items: lineItems.map((line, idx) => ({
        lineNumber: idx + 1,
        vin: line.sku,
        sku: line.sku,
        itemNumber: line.gfsItemNumber,
        qty: Number(line.qty) || 0,
        quantity: Number(line.qty) || 0,
        description: line.description,
        unitCost:
          line.unitCost === ''
            ? null
            : Number(String(line.unitCost).replace(/,/g, '')),
        unit: normalizeUomForUi(line.unit),
        originalQty: line.originalQty,
        originalUnitCost: line.originalUnitCost,
        appliedUomRule: line.appliedUomRule,
        expectedPrice: line.expectedPrice,
        priceValidation: line.priceValidation
      }))
    };

    try {
      const res = await axios.put(
        `${API_BASE}api/po/history/${item.id}`,
        {
          originalFileName: item.originalFileName || item.name,
          structuredData,
          // 🆕 Root-level operator decisions (also handled by backend)
          manualWarehouse: warehouseId || null,
          manualShippingDeadline: manualShippingDeadline || null,
          highValueConfirmed: Boolean(highValueConfirmed),
          highValueCustomDays:
            highValueCustomDays === '' || highValueCustomDays == null
              ? null
              : Number(highValueCustomDays)
        },
        { headers: getAuthHeaders() }
      );

      if (res.data.success) {
        onSaved();
        onClose();
      } else {
        setError(res.data.message || 'Failed to update record.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update order.');
    } finally {
      setSaving(false);
    }
  };

  const hasMismatchesInUi = lineItems.some(
    (i) =>
      i.isMismatch ||
      (i.expectedPrice &&
        Math.abs(Number(String(i.unitCost).replace(/,/g, '')) - i.expectedPrice) > 0.01)
  );

  const warehouseMissing = !warehouseId;
  const deadlinePast = isDeadlineInPast(effectiveDeadline);

  return (
    <>
      <div
        className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] lg:max-w-7xl max-h-[92vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Edit Order</h2>
              <p className="text-sm text-gray-500 mt-1">{item.originalFileName || item.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {hasMismatchesInUi && (
              <div className="border border-amber-300 bg-amber-50 rounded-xl p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider">
                        Price Discrepancy Detected
                      </h3>
                      <p className="text-xs text-amber-800 mt-1">
                        One or more line items do not match ERPAG Price Tier rates. Review the
                        highlighted items below.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyErpagPrices}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition-colors shadow-sm shrink-0"
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Update All to ERPAG Prices
                  </button>
                </div>

                <div className="mt-4 pt-3 border-t border-amber-200 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="overrideAuthCheckbox"
                    checked={overrideAuthorized}
                    onChange={(e) => setOverrideAuthorized(e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500 cursor-pointer"
                  />
                  <label
                    htmlFor="overrideAuthCheckbox"
                    className="text-xs font-bold text-amber-900 cursor-pointer select-none"
                  >
                    I have confirmed the price discrepancy with the customer and authorize syncing
                    with PO prices.
                  </label>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">
                Order Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Customer / Bill To
                  </label>
                  <input
                    type="text"
                    value={header.customer}
                    onChange={(e) => setHeader({ ...header, customer: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">PO (TAG)</label>
                  <input
                    type="text"
                    value={header.poNumber}
                    onChange={(e) => setHeader({ ...header, poNumber: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Requested Delivery Date
                  </label>
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={header.requestedDeliveryDate}
                    onChange={(e) =>
                      setHeader({ ...header, requestedDeliveryDate: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Cancel Date
                  </label>
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={header.cancelDate}
                    onChange={(e) => setHeader({ ...header, cancelDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Currency</label>
                  <select
                    value={header.currency}
                    onChange={(e) => setHeader({ ...header, currency: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </div>

            {/* SHIPPING DEADLINE & ROUTING */}
            <div className="border border-gray-200 rounded-xl bg-gray-50/60 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-gray-700" />
                <div>
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                    Shipping Deadline & Routing
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Auto-calculated from customer family, transit levels, and order dates.
                  </p>
                </div>
              </div>

              {routingInfo ? (
                <>
                  {/* Exception Banners — handles object OR string */}
                  {routingInfo.exceptions && routingInfo.exceptions.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {routingInfo.exceptions.map((ex, i) => {
                        const msg = typeof ex === 'string' ? ex : ex?.message || ex?.code || 'Unknown exception';
                        const code = typeof ex === 'object' ? ex?.code : null;
                        return (
                          <div
                            key={i}
                            className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800"
                          >
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <span>
                              {code && (
                                <span className="font-bold mr-1">[{code}]</span>
                              )}
                              {msg}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {deadlinePast && (
                    <div className="mb-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800">
                      <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      <span>
                        Calculated shipping deadline is in the past. Review and set a manual
                        override if needed.
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Deadline */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-gray-500" />
                          <span className="text-[10px] font-bold text-gray-500 uppercase">
                            Ship-By Deadline
                          </span>
                        </div>
                        {manualShippingDeadline && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">
                            MANUAL OVERRIDE
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {formatDeadlineLabel(effectiveDeadline)}
                      </div>
                      <input
                        type="date"
                        value={manualShippingDeadline || ''}
                        onChange={(e) => setManualShippingDeadline(e.target.value)}
                        className="w-full mt-2 border border-gray-200 rounded px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-blue-500"
                        title="Manual shipping deadline override"
                      />
                      {manualShippingDeadline && (
                        <button
                          type="button"
                          onClick={() => setManualShippingDeadline('')}
                          className="text-[10px] text-blue-600 hover:underline mt-1"
                        >
                          Clear manual override
                        </button>
                      )}
                    </div>

                    {/* Priority */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Shield className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase">
                          Priority
                        </span>
                      </div>
                      {(() => {
                        const cfg = getPriorityConfig(routingInfo.priorityUsed);
                        return (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${cfg.color}`}
                          >
                            {cfg.label}
                          </span>
                        );
                      })()}
                      {routingInfo.status && routingInfo.status !== 'OK' && (
                        <div className="text-[10px] text-red-600 font-semibold mt-1">
                          {routingInfo.status.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>

                    {/* Transit */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Package className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase">
                          Transit
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-gray-800">
                        {routingInfo.transitLevel
                          ? `${routingInfo.transitLevel} (${routingInfo.transitDays}d)`
                          : '—'}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        {routingInfo.deliveryRegion || 'No region matched'}
                      </div>
                    </div>

                    {/* Warehouse */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <Truck className="w-3.5 h-3.5 text-gray-500" />
                          <span className="text-[10px] font-bold text-gray-500 uppercase">
                            Warehouse
                          </span>
                        </div>
                        {warehouseMissing && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">
                            REQUIRED
                          </span>
                        )}
                      </div>
                      {warehouses.length > 0 ? (
                        <select
                          value={warehouseId}
                          onChange={handleWarehouseChange}
                          className={`w-full border rounded px-2 py-1 text-xs font-semibold bg-white focus:ring-2 focus:ring-blue-500 ${
                            warehouseMissing ? 'border-red-300' : 'border-gray-300'
                          }`}
                        >
                          <option value="">— Select Warehouse —</option>
                          {warehouses.map((wh) => (
                            <option key={wh.id} value={wh.id}>
                              {wh.description || wh.key} {wh.key ? `(${wh.key})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={warehouseLabel}
                          onChange={(e) => {
                            setWarehouseLabel(e.target.value);
                            setWarehouseId('');
                          }}
                          placeholder="Warehouse name"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-semibold bg-white focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  </div>

                  {/* High-value row */}
                  {needsHighValueGate && (
                    <div className="mt-4 flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-red-600" />
                        <span className="text-xs font-semibold text-red-900">
                          High-value P4 order (${formatCurrency(totalAmount)}). Confirmation
                          required.
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowHighValueModal(true)}
                        className={`text-xs font-bold px-3 py-1.5 rounded border ${
                          highValueConfirmed
                            ? 'bg-green-50 border-green-300 text-green-800'
                            : 'bg-red-600 border-red-700 text-white hover:bg-red-700'
                        }`}
                      >
                        {highValueConfirmed
                          ? `✓ Confirmed (${highValueCustomDays || DEFAULT_FALLBACK_BUSINESS_DAYS}d)`
                          : 'Review & Confirm Deadline'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-gray-400 italic flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Shipping deadline not calculated for this order. Save and refresh to compute.
                </div>
              )}
            </div>

            <div className="border border-blue-200 rounded-xl bg-blue-50/40 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                      Ship To
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Required. This address is used for ERPAG delivery.
                    </p>
                  </div>
                </div>
                <span className="text-xs font-bold text-red-600">REQUIRED</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Ship To Name *
                  </label>
                  <input
                    type="text"
                    value={shipTo.name}
                    onChange={(e) => updateShipTo('name', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Address 1 *
                  </label>
                  <input
                    type="text"
                    value={shipTo.address1}
                    onChange={(e) => updateShipTo('address1', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Address 2
                  </label>
                  <input
                    type="text"
                    value={shipTo.address2}
                    onChange={(e) => updateShipTo('address2', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">City *</label>
                  <input
                    type="text"
                    value={shipTo.city}
                    onChange={(e) => updateShipTo('city', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    State / Province
                  </label>
                  <input
                    type="text"
                    value={shipTo.stateProvince}
                    onChange={(e) => updateShipTo('stateProvince', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Postal / ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={shipTo.zipCode}
                    onChange={(e) => updateShipTo('zipCode', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Country *
                  </label>
                  <select
                    value={shipTo.country}
                    onChange={(e) => updateShipTo('country', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select country</option>
                    <option value="Canada">Canada</option>
                    <option value="United States">United States</option>
                    <option value="Mexico">Mexico</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                  Order Items
                </h3>
                <button
                  onClick={addLine}
                  className="flex items-center px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
                </button>
              </div>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-left whitespace-nowrap">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-xs font-bold text-gray-600 uppercase min-w-[130px]">
                        SKU
                      </th>
                      <th className="px-3 py-2 text-xs font-bold text-gray-600 uppercase min-w-[120px]">
                        Customer Article #
                      </th>
                      <th className="px-3 py-2 text-xs font-bold text-gray-600 uppercase min-w-[80px]">
                        Qty
                      </th>
                      <th className="px-3 py-2 text-xs font-bold text-gray-600 uppercase min-w-[80px]">
                        UOM
                      </th>
                      <th className="px-3 py-2 text-xs font-bold text-gray-600 uppercase w-full">
                        Description
                      </th>
                      <th className="px-3 py-2 text-xs font-bold text-gray-600 uppercase min-w-[100px]">
                        Cost
                      </th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lineItems.map((line, idx) => {
                      const lineVal = Number(String(line.unitCost).replace(/,/g, ''));
                      const isMismatched =
                        line.expectedPrice && Math.abs(lineVal - line.expectedPrice) > 0.01;

                      return (
                        <tr key={idx} className={isMismatched ? 'bg-amber-50/40' : ''}>
                          <td className="px-2 py-1">
                            <input
                              value={line.sku}
                              onChange={(e) => updateLine(idx, 'sku', e.target.value)}
                              className="w-full min-w-[130px] border border-gray-200 rounded px-2 py-1.5 text-sm font-mono bg-white"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={line.gfsItemNumber}
                              onChange={(e) => updateLine(idx, 'gfsItemNumber', e.target.value)}
                              className="w-full min-w-[120px] border border-gray-200 rounded px-2 py-1.5 text-sm font-mono bg-white"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              min="0"
                              value={line.qty}
                              onChange={(e) => updateLine(idx, 'qty', e.target.value)}
                              className="w-full min-w-[70px] border border-gray-200 rounded px-2 py-1.5 text-sm text-center font-semibold bg-white"
                            />
                            {line.originalQty !== null &&
                              line.originalQty !== undefined &&
                              Number(line.originalQty) !== Number(line.qty) && (
                                <span
                                  className="block text-[10px] text-blue-600 text-center font-semibold mt-0.5"
                                  title="Original quantity captured from PO document"
                                >
                                  PO: {line.originalQty}
                                </span>
                              )}
                          </td>
                          <td className="px-2 py-1">
                            <select
                              value={normalizeUomForUi(line.unit)}
                              onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                              className="w-full min-w-[80px] border border-gray-200 rounded px-2 py-1.5 text-sm font-semibold bg-white"
                            >
                              <option value="UN">UN</option>
                              <option value="CS">CS</option>
                              <option value="MASTER">MASTER</option>
                              <option value="KG">KG</option>
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <input
                              value={line.description}
                              onChange={(e) => updateLine(idx, 'description', e.target.value)}
                              className="w-full min-w-[150px] border border-gray-200 rounded px-2 py-1.5 text-sm bg-white"
                            />
                          </td>

                          <td className="px-2 py-1">
                            <input
                              type="text"
                              value={line.unitCost}
                              onChange={(e) => updateLine(idx, 'unitCost', e.target.value)}
                              onBlur={(e) =>
                                updateLine(idx, 'unitCost', formatCurrency(e.target.value))
                              }
                              className={`w-full min-w-[110px] border rounded px-2 py-1.5 text-sm text-center font-semibold ${
                                isMismatched
                                  ? 'border-amber-400 bg-amber-50 text-amber-900 focus:ring-amber-500'
                                  : 'border-gray-200 bg-white focus:ring-blue-500'
                              }`}
                            />
                            {isMismatched && (
                              <span className="block text-[10px] text-amber-700 font-bold text-center mt-0.5">
                                Tier Rate: ${formatCurrency(line.expectedPrice)}
                              </span>
                            )}
                            {!isMismatched &&
                              line.originalUnitCost !== null &&
                              line.originalUnitCost !== undefined &&
                              Number(line.originalUnitCost) !==
                                Number(String(line.unitCost).replace(/,/g, '')) && (
                                <span
                                  className="block text-[10px] text-blue-600 text-center font-semibold mt-0.5"
                                  title="Original price captured from PO document"
                                >
                                  PO: ${formatCurrency(line.originalUnitCost)}
                                </span>
                              )}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button
                              onClick={() => removeLine(idx)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {lineItems.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-400">
                          No items. Click “Add Item”.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                    <tr>
                      <td
                        colSpan="2"
                        className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider"
                      >
                        Totals:
                      </td>
                      <td colSpan="2" className="px-2 py-3">
                        <div className="flex items-center justify-center gap-4 text-sm font-bold text-gray-900 bg-white border border-gray-200 rounded-lg py-1 px-2 shadow-sm">
                          <span className="text-blue-700">{totalUN} UN</span>
                          <span className="text-purple-700">{totalCS} CS</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Total Amount:
                      </td>
                      <td className="px-2 py-3 text-center">
                        <div className="text-sm font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg py-1 px-2 shadow-sm">
                          ${formatCurrency(totalAmount)}
                        </div>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
            {warehouseMissing && (
              <div className="mb-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-xs font-semibold text-red-800">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                Target warehouse must be selected before saving (required for ERPAG export).
              </div>
            )}
            {needsHighValueGate && !highValueConfirmed && (
              <div className="mb-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-xs font-semibold text-red-800">
                <Shield className="w-4 h-4 text-red-600" />
                High-value P4 order requires confirmation of the shipping deadline before saving.
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || warehouseMissing || !highValueSatisfied}
                className="flex items-center px-5 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <HighValueConfirmModal
        isOpen={showHighValueModal}
        onClose={() => setShowHighValueModal(false)}
        onConfirm={handleHighValueConfirm}
        orderTotal={totalAmount}
        soDate={item.uploadedAt}
        currentDeadline={effectiveDeadline}
        initialCustomDays={highValueCustomDays}
      />
    </>
  );
};

// ============================================================
// HELPER: GET FILE PREVIEW URL
// ============================================================
const getFileUrl = (item) => {
  if (String(item.source || '').toUpperCase() === 'FAXINATING') return null;
  if (item.downloadUrl) return item.downloadUrl;
  if (!item.filePath) return null;

  const normalized = String(item.filePath).replace(/\\/g, '/').replace(/^\/+/, '');

  const publicPath = normalized.replace(/^src\/uploads\//i, '').replace(/^uploads\//i, '');

  const baseUrl = API_BASE.replace(/\/$/, '');
  return `${baseUrl}/files/${publicPath}`;
};

// ============================================================
// HELPER: FORMAT UOM RULE BADGE
// ============================================================
const renderUomBadge = (rule) => {
  if (!rule || typeof rule !== 'object') return null;
  const ordering = rule.unitOrdering || '';
  const mult = rule.multiple
    ? ` (${rule.unitOrdering === 'UNIT_OVERRIDE' ? '÷' : '×'}${rule.multiple})`
    : '';

  if (ordering === 'UNIT_OVERRIDE') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200">
        Unit Override{mult}
      </span>
    );
  }
  if (ordering === 'CS_OVERRIDE') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
        CS Override{mult}
      </span>
    );
  }
  if (ordering === 'REGEX_FALLBACK') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
        Regex Fallback{mult}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200">
      {ordering || 'Standard'}
    </span>
  );
};

// ============================================================
// UPLOAD HISTORY
// ============================================================
const UploadHistory = ({ uploads, onRefresh, selectedIds, setSelectedIds }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  
  // 🆕 Task C: Consolidation Drawer Toggle State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopy = async (value, key) => {
    if (!value || value === 'N/A' || value === '—') return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(String(value));
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = String(value);
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedField(key);
      setTimeout(() => setCopiedField(''), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyColumn = async (type) => {
    let values = [];
    const targetUploads =
      selectedIds && selectedIds.length > 0
        ? uploads.filter((u) => selectedIds.includes(u.id))
        : uploads;
    if (type === 'po') {
      values = targetUploads
        .map((u) => u.structuredData?.purchaseOrder?.poNumber || u.poNumber || '—')
        .filter((v) => v && v !== '—');
    } else if (type === 'so') {
      values = targetUploads.map((u) => u.erpagSO).filter((v) => v && v !== '—' && v !== undefined);
    }
    if (values.length === 0) return;
    const textToCopy = values.join('\n');
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedField(`col-${type}`);
      setTimeout(() => setCopiedField(''), 1500);
    } catch (err) {
      console.error('Failed to copy column:', err);
    }
  };

  const handleToggleSelection = (id) => {
    if (setSelectedIds) {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    }
  };

  const safeSelectedIds = selectedIds || [];
  const allSelected = uploads.length > 0 && safeSelectedIds.length === uploads.length;
  const toggleSelectAll = () => {
    if (setSelectedIds) setSelectedIds(allSelected ? [] : uploads.map((u) => u.id));
  };

  const apiCall = async (method, url, body) => {
    const res = await axios({
      method,
      url: `${API_BASE}${url}`,
      data: body,
      headers: getAuthHeaders()
    });
    return res.data;
  };

  // 🆕 Centralized error handler that intercepts backend gate codes
  const handleBackendGateError = (item, errData) => {
    const code = errData?.code;
    const message = errData?.message || '';
    if (code === 'HIGH_VALUE_CONFIRMATION_REQUIRED') {
      alert(
        `${message}\n\nClick OK to open the order and confirm the deadline for this high-value P4 order.`
      );
      setEditingItem(item);
      return true;
    }
    if (code === 'WAREHOUSE_REQUIRED') {
      alert(`${message}\n\nClick OK to open the order and select a target warehouse.`);
      setEditingItem(item);
      return true;
    }
    return false;
  };

  const handleApprove = async (item) => {
    const priceSummary = item.structuredData?.priceValidationSummary;
    if (priceSummary?.hasMismatch && !priceSummary?.overrideAuthorized) {
      alert(
        'Cannot approve this order due to an unauthorized Price Mismatch. Please edit the order to authorize or update prices.'
      );
      return;
    }

    // 🆕 Front-end short-circuit for high-value P4
    const routing = item.routingInfo;
    if (
      routing?.requiresHighValueConfirmation &&
      !item.structuredData?.highValueConfirmed
    ) {
      alert(
        'This high-value P4 order requires deadline confirmation before approve. Opening editor…'
      );
      setEditingItem(item);
      return;
    }

    setBusyId(item.id);
    try {
      const r = await apiCall('post', `api/po/history/${item.id}/approve`);
      if (!r.success) alert(r.message || 'Approve failed.');
      onRefresh();
    } catch (e) {
      const data = e.response?.data;
      if (!handleBackendGateError(item, data)) {
        alert(data?.message || 'Approve failed.');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleSync = async (item) => {
    const shipTo = item.structuredData?.shipTo;
    const address1 =
      shipTo?.address1 || shipTo?.streetLines?.[0] || shipTo?.addressLines?.[0] || '';
    const city = shipTo?.city || '';
    const postal = shipTo?.postalCode || shipTo?.zipCode || '';
    const name = shipTo?.name || '';

    if (!name || !address1 || !city || !postal) {
      alert(
        'Cannot sync this PO because the mandatory Ship To address is missing. Please edit the PO and enter the complete Ship To address.'
      );
      return;
    }

    const priceSummary = item.structuredData?.priceValidationSummary;
    if (priceSummary?.hasMismatch && !priceSummary?.overrideAuthorized) {
      alert(
        'SYNC BLOCKED: This PO has a price mismatch with ERPAG Price Tier rates. Please click the Customer Name to open Edit Order, then click "Update All to ERPAG Prices" OR check the authorization box.'
      );
      return;
    }

    // 🆕 Front-end short-circuit for warehouse / high-value
    const sd = item.structuredData || {};
    const routing = item.routingInfo;
    const resolvedWarehouse =
      sd.manualWarehouse || sd.warehouse || routing?.assignedWarehouse || '';
    if (!resolvedWarehouse) {
      alert(
        'SYNC BLOCKED: Target warehouse must be selected before ERPAG export. Opening editor…'
      );
      setEditingItem(item);
      return;
    }
    if (
      routing?.requiresHighValueConfirmation &&
      !sd.highValueConfirmed
    ) {
      alert(
        'SYNC BLOCKED: This high-value P4 order requires deadline confirmation before sync. Opening editor…'
      );
      setEditingItem(item);
      return;
    }

    setBusyId(item.id);
    try {
      const r = await apiCall('post', `api/po/history/${item.id}/sync`);
      if (!r.success) alert(r.message || 'Sync failed.');
      onRefresh();
    } catch (e) {
      const data = e.response?.data;
      if (!handleBackendGateError(item, data)) {
        alert(data?.message || 'Sync failed.');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.originalFileName || item.name}"?`))
      return;
    setBusyId(item.id);
    try {
      const r = await apiCall('delete', `api/po/history/${item.id}`);
      if (!r.success) alert(r.message || 'Delete failed.');
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {/* 🆕 Tier 4: Consolidation Toolbar */}
      <div className="flex justify-end mb-3">
        <button
          onClick={() => setIsDrawerOpen(true)}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
        >
          <Layers className="w-4 h-4 mr-2" />
          Consolidation Quick-View
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left border-collapse whitespace-nowrap">
            <thead className="bg-black text-white">
              <tr>
                <th className="px-4 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">Date</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">Customer</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">Source</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">
                  <div className="flex items-center gap-2">
                    PO (TAG)
                    <button
                      onClick={() => handleCopyColumn('po')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedField === 'col-po' ? (
                        <Check className="h-3.5 w-3.5 text-green-400" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </th>
                {/* 🆕 Deadline column */}
                <th className="px-4 py-4 text-xs font-semibold uppercase">Ship-By</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase text-right">Amount</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">Currency</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">Automation Status</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">
                  <div className="flex items-center gap-2">
                    ERPAG SO
                    <button
                      onClick={() => handleCopyColumn('so')}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Copy ERPAG Sales Orders"
                    >
                      {copiedField === 'col-so' ? (
                        <Check className="h-3.5 w-3.5 text-green-400" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </th>
                <th className="px-4 py-4 text-xs font-semibold uppercase">Imp./Appr. By</th>
                <th className="px-4 py-4 text-xs font-semibold uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {uploads.map((item, index) => {
                const sd = item.structuredData || {};
                const priceSummary = sd.priceValidationSummary || {};
                const isBlockedByPrice =
                  priceSummary.hasMismatch && !priceSummary.overrideAuthorized;
                const routing = item.routingInfo || null;

                let customer =
                  sd.billTo?.name ||
                  sd.customer?.name ||
                  sd.customer ||
                  item.customer ||
                  'Gordon Food Service Canada Ltd';
                if (typeof customer === 'object') customer = 'Gordon Food Service Canada Ltd';

                const poTag = sd.purchaseOrder?.poNumber || item.poNumber || '—';
                const amount =
                  item.orderAmount ??
                  (sd.items || []).reduce(
                    (sum, li) =>
                      sum +
                      (Number(li.unitCost) || 0) * (Number(li.qty ?? li.quantity) || 0),
                    0
                  );
                const currency = sd.totals?.currency || item.currency || 'CAD';
                const isBusy = busyId === item.id;

                const statusVal = String(item.status || '').toUpperCase();
                const isApprovedOrSynced = ['APPROVED', 'SYNCED', 'COMPLETED'].includes(statusVal);

                const importedOrApprovedBy = isApprovedOrSynced
                  ? item.approvedByName ||
                    item.importedByName ||
                    (item.source === 'FAXINATING' ? 'Faxinating EDI' : 'System')
                  : item.importedByName ||
                    (item.source === 'FAXINATING' ? 'Faxinating EDI' : 'System');

                // Deadline cell computation
                const effectiveDeadline =
                  sd.manualShippingDeadline || routing?.shippingDeadline || '';
                const priorityCfg = getPriorityConfig(routing?.priorityUsed);
                const deadlinePast = isDeadlineInPast(effectiveDeadline);
                const needsHvAttn =
                  routing?.requiresHighValueConfirmation && !sd.highValueConfirmed;

                return (
                  <React.Fragment key={item.id || index}>
                    <tr
                      className={`hover:bg-gray-50 transition-colors ${
                        safeSelectedIds.includes(item.id) ? 'bg-gray-50' : ''
                      } ${isBlockedByPrice ? 'bg-amber-50/20' : ''}`}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={safeSelectedIds.includes(item.id)}
                          onChange={() => handleToggleSelection(item.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-800">
                        {item.uploadedAt ? new Date(item.uploadedAt).toLocaleDateString() : '—'}
                      </td>

                      <td className="px-4 py-4 text-sm font-semibold text-gray-800">
                        <button
                          onClick={() => setEditingItem(item)}
                          className="text-left text-gray-800 hover:text-blue-600 hover:underline focus:outline-none transition-colors flex items-center gap-1.5"
                          title="Click to view/edit order details"
                        >
                          {customer}
                          {isBlockedByPrice && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                              <AlertTriangle className="w-3 h-3 mr-0.5 text-amber-600" />
                              Price Error
                            </span>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {(() => {
                          const meta = getSourceMeta(item.source);
                          return (
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${meta.className}`}
                            >
                              {meta.label}
                            </span>
                          );
                        })()}
                      </td>

                      <td className="px-4 py-4 text-sm font-medium text-gray-800">
                        <div className="flex items-center gap-1.5">
                          {getFileUrl(item) ? (
                            <a
                              href={getFileUrl(item)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-blue-600 hover:text-blue-800 hover:underline"
                              title="Click to preview original document"
                            >
                              {poTag}
                            </a>
                          ) : (
                            <span className="font-mono">{poTag}</span>
                          )}
                          <button
                            onClick={() => handleCopy(poTag, `po-${item.id}`)}
                            className="text-gray-400 hover:text-gray-700"
                          >
                            {copiedField === `po-${item.id}` ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* 🆕 Ship-By column */}
                      <td className="px-4 py-4 text-sm">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs font-semibold text-gray-800">
                              {formatDeadlineLabel(effectiveDeadline)}
                            </span>
                            {sd.manualShippingDeadline && (
                              <span
                                className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200"
                                title="Manual override"
                              >
                                M
                              </span>
                            )}
                            {deadlinePast && (
                              <span
                                className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200"
                                title="Deadline is in the past"
                              >
                                PAST
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-wrap">
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${priorityCfg.color}`}
                            >
                              {priorityCfg.label.split('–')[0].trim()}
                            </span>
                            {needsHvAttn && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border bg-red-100 text-red-800 border-red-200">
                                HV CONFIRM
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-sm font-bold text-gray-800 text-right">
                        {typeof amount === 'number' ? formatCurrency(amount) : '—'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 font-medium">{currency}</td>
                      <td className="px-4 py-4 text-sm font-semibold">
                        {isBlockedByPrice ? (
                          <span className="text-amber-700 font-bold flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                            Price Mismatch
                          </span>
                        ) : (
                          <span className="text-black">
                            {getStatusLabel(item.status, priceSummary)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-800">
                        {item.erpagSO ? (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-bold text-green-700">
                              {item.erpagSO}
                            </span>
                            <button
                              onClick={() => handleCopy(item.erpagSO, `so-${item.id}`)}
                              className="text-gray-400 hover:text-gray-700"
                              title="Copy ERPAG Sales Order"
                            >
                              {copiedField === `so-${item.id}` ? (
                                <Check className="h-3 w-3 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 font-medium">
                        {importedOrApprovedBy}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1.5 flex-nowrap">
                          {item.status !== 'SYNCED' &&
                            item.status !== 'APPROVED' &&
                            item.status !== 'COMPLETED' && (
                              <button
                                onClick={() => handleApprove(item)}
                                disabled={isBusy || isBlockedByPrice}
                                className="px-2 py-1.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-semibold hover:bg-indigo-100 border border-indigo-200 disabled:opacity-40"
                              >
                                Approve
                              </button>
                            )}
                          <button
                            onClick={() => setEditingItem(item)}
                            disabled={isBusy}
                            title="Edit PO"
                            className="flex items-center p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 disabled:opacity-50"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          {(item.status === 'FAILED' ||
                            item.status === 'APPROVED' ||
                            item.status === 'COMPLETED' ||
                            item.status === 'NEEDS_REVIEW') && (
                            <button
                              onClick={() => handleSync(item)}
                              disabled={isBusy || isBlockedByPrice}
                              title={
                                isBlockedByPrice
                                  ? 'Sync blocked: Resolve price mismatch first'
                                  : 'Sync to ERPAG'
                              }
                              className="flex items-center px-2 py-1.5 rounded bg-purple-50 text-purple-700 text-[10px] font-semibold hover:bg-purple-100 border border-purple-200 disabled:opacity-40 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200"
                            >
                              <RefreshCw
                                className={`w-3.5 h-3.5 mr-1 ${isBusy ? 'animate-spin' : ''}`}
                              />
                              Sync
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(item)}
                            disabled={isBusy}
                            title="Delete PO"
                            className="flex items-center p-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => toggleRow(item.id)}
                            className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 ml-1"
                          >
                            {expandedRows[item.id] ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {expandedRows[item.id] && (
                      <tr className="bg-gray-50/50">
                        <td colSpan="12" className="px-8 py-6 border-b border-gray-200">
                          <div className="bg-white border border-gray-300 rounded-xl p-6 shadow-sm">
                            {isBlockedByPrice && (
                              <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex items-start text-amber-900">
                                <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-amber-600" />
                                <div>
                                  <h4 className="text-sm font-bold">Price Discrepancy Detected</h4>
                                  <p className="text-sm mt-0.5">
                                    {priceSummary.mismatchCount || 1} line item(s) differ from
                                    ERPAG Price Tier rates. Click{' '}
                                    <button
                                      onClick={() => setEditingItem(item)}
                                      className="font-bold underline hover:text-blue-700"
                                    >
                                      Edit Order
                                    </button>{' '}
                                    to review and authorize override or update prices.
                                  </p>
                                </div>
                              </div>
                            )}

                            {item.status === 'FAILED' && item.syncError && !isBlockedByPrice && (
                              <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 flex items-start text-red-800">
                                <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                                <div>
                                  <h4 className="text-sm font-bold">Sync Failed</h4>
                                  <p className="text-sm mt-1 whitespace-pre-wrap">
                                    {item.syncError}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* 🆕 Routing exceptions summary */}
                            {routing?.exceptions && routing.exceptions.length > 0 && (
                              <div className="mb-4 space-y-2">
                                {routing.exceptions.map((ex, i) => {
                                  const msg =
                                    typeof ex === 'string'
                                      ? ex
                                      : ex?.message || ex?.code || 'Unknown exception';
                                  const code = typeof ex === 'object' ? ex?.code : null;
                                  return (
                                    <div
                                      key={i}
                                      className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800"
                                    >
                                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                      <span>
                                        {code && (
                                          <span className="font-bold mr-1">[{code}]</span>
                                        )}
                                        {msg}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <div className="mb-6 border border-blue-200 bg-blue-50 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <MapPin className="w-4 h-4 text-blue-600" />
                                <h4 className="text-sm font-bold text-gray-800">Ship To</h4>
                              </div>
                              {sd.shipTo?.name ? (
                                <div className="text-sm text-gray-700 whitespace-normal">
                                  <div className="font-semibold">{sd.shipTo.name}</div>
                                  <div>
                                    {sd.shipTo.streetLines?.join(', ') ||
                                      sd.shipTo.address1 ||
                                      '—'}
                                  </div>
                                  <div>
                                    {sd.shipTo.city || ''}
                                    {sd.shipTo.region ? `, ${sd.shipTo.region}` : ''}
                                    {sd.shipTo.postalCode ? ` ${sd.shipTo.postalCode}` : ''}
                                  </div>
                                  <div>{sd.shipTo.country || sd.shipTo.countryCode || ''}</div>
                                </div>
                              ) : (
                                <div className="text-sm text-red-600 font-semibold">
                                  Ship To address is missing. This PO cannot be synced.
                                </div>
                              )}
                            </div>

                            <p className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">
                              Order Items for {poTag}
                            </p>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                              <table className="min-w-full text-left bg-white whitespace-nowrap">
                                <thead className="bg-gray-100 border-b border-gray-200">
                                  <tr>
                                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase">
                                      SKU
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase">
                                      Customer Article #
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-blue-700 uppercase bg-blue-50/50">
                                      Original PO Data
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase">
                                      ERPAG Qty
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase">
                                      UOM
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase">
                                      Description
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase text-right">
                                      PO Unit Cost
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-amber-800 uppercase text-right bg-amber-50/50">
                                      ERPAG Tier Rate
                                    </th>
                                    <th className="px-4 py-3 text-xs font-bold text-gray-700 uppercase text-center">
                                      UOM Rule
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {(sd.items || []).length > 0 ? (
                                    sd.items.map((li, idx) => {
                                      const origQty = li.originalQty ?? li.excelUnits;
                                      const origCost = li.originalUnitCost ?? li.excelFirstCost;
                                      const rule = li.appliedUomRule || li.uomRule;

                                      const tierPrice =
                                        li.expectedPrice ?? li.priceValidation?.expectedPrice;
                                      const lineVal = Number(li.unitCost ?? li.price);
                                      const isMismatched =
                                        tierPrice && Math.abs(lineVal - tierPrice) > 0.01;

                                      return (
                                        <tr
                                          key={idx}
                                          className={`hover:bg-gray-50 ${
                                            isMismatched ? 'bg-amber-50/30' : ''
                                          }`}
                                        >
                                          <td className="px-4 py-3 text-sm font-mono">
                                            {li.vin || li.sku || '—'}
                                          </td>
                                          <td className="px-4 py-3 text-sm font-mono">
                                            {li.itemNumber || '—'}
                                          </td>

                                          <td className="px-4 py-3 text-sm bg-blue-50/30 font-medium text-blue-900">
                                            {origQty !== undefined && origQty !== null ? (
                                              <span>
                                                {origQty}{' '}
                                                <span className="text-gray-400">@</span> $
                                                {formatCurrency(origCost)}
                                              </span>
                                            ) : (
                                              <span className="text-gray-400">—</span>
                                            )}
                                          </td>

                                          <td className="px-4 py-3 text-sm font-bold">
                                            {li.qty ?? li.quantity ?? '—'}
                                          </td>
                                          <td className="px-4 py-3 text-sm font-semibold text-gray-700">
                                            {normalizeUomForUi(li.unit || li.uom)}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-600">
                                            {li.description || '—'}
                                          </td>

                                          <td
                                            className={`px-4 py-3 text-sm font-bold text-right ${
                                              isMismatched ? 'text-amber-800' : 'text-gray-900'
                                            }`}
                                          >
                                            {li.unitCost !== null && li.unitCost !== undefined
                                              ? `$${formatCurrency(li.unitCost)}`
                                              : '—'}
                                          </td>

                                          <td className="px-4 py-3 text-sm font-bold text-right bg-amber-50/30 text-amber-900">
                                            {tierPrice ? `$${formatCurrency(tierPrice)}` : '—'}
                                          </td>

                                          <td className="px-4 py-3 text-sm text-center">
                                            {renderUomBadge(rule)}
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan="9"
                                        className="px-4 py-4 text-sm text-center text-gray-500"
                                      >
                                        No line items found.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>

                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                  <tr>
                                    <td
                                      colSpan="3"
                                      className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider"
                                    >
                                      Totals:
                                    </td>
                                    <td colSpan="2" className="px-4 py-3 text-left">
                                      <div className="inline-flex items-center gap-4 text-sm font-bold text-gray-900 bg-white border border-gray-200 rounded-lg py-1 px-3">
                                        <span className="text-blue-700">
                                          {(sd.items || [])
                                            .filter(
                                              (i) =>
                                                normalizeUomForUi(i.unit || i.uom) === 'UN'
                                            )
                                            .reduce(
                                              (sum, i) =>
                                                sum + (Number(i.qty ?? i.quantity) || 0),
                                              0
                                            )}{' '}
                                          UN
                                        </span>
                                        <span className="text-purple-700">
                                          {(sd.items || [])
                                            .filter(
                                              (i) =>
                                                normalizeUomForUi(i.unit || i.uom) === 'CS'
                                            )
                                            .reduce(
                                              (sum, i) =>
                                                sum + (Number(i.qty ?? i.quantity) || 0),
                                              0
                                            )}{' '}
                                          CS
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                                      Total Order Value:
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="inline-block text-sm font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg py-1 px-3">
                                        $
                                        {formatCurrency(
                                          (sd.items || []).reduce(
                                            (sum, i) =>
                                              sum +
                                              (Number(i.qty ?? i.quantity) || 0) *
                                                (Number(i.unitCost ?? i.price) || 0),
                                            0
                                          )
                                        )}
                                      </div>
                                    </td>
                                    <td colSpan="2"></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {uploads.length === 0 && (
                <tr>
                  <td colSpan="12" className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 mb-3 text-gray-300 mx-auto" />
                    <p className="text-base font-medium text-gray-500">No orders found.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {editingItem && (
        <EditOrderModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={onRefresh}
        />
      )}

      {/* 🆕 Tier 4: Consolidation Drawer Implementation */}
      <ConsolidationDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        uploads={
          safeSelectedIds.length > 0
            ? uploads.filter((u) => safeSelectedIds.includes(u.id))
            : uploads
        }
        isFiltered={safeSelectedIds.length > 0}
      />
    </>
  );
};

export default UploadHistory;